import moment from 'moment'
import { forwardRef, Fragment, useCallback, useEffect, useLayoutEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import WithReports from './WithReports'
import { formateDate, startEndTruncate, walletLink } from 'utils'
import _, { toLower, map, join, startCase, get } from 'lodash'
import classNames from 'classnames'
import { Tooltip } from 'antd'
import { IoInformationCircleSharp } from 'react-icons/io5'
import { Dialog, Transition, Menu } from '@headlessui/react'
import { XIcon, CalendarIcon, PlayIcon, StopIcon, DownloadIcon, DotsHorizontalIcon, CheckCircleIcon } from '@heroicons/react/outline'
import apiGet from 'lib/network/apiGet'
import apiPost from 'lib/network/apiPost'
import queryString from 'query-string'
import { useHistory, useParams } from 'react-router-dom'
import BookingLogsDrawer from 'pages/booking/bookingLogs'
import BookingsStream from 'lib/network/apiBookingsStream'
import { useSelector } from 'react-redux'
import BookingNotesDrawer from 'pages/booking/bookingNotes'
import { useTranslation } from 'react-i18next'
import apiGetBlob from 'lib/network/apiGetBlob'
import { formatPrice } from 'lib/utils'
import CompanyStatsSummary from 'components/company/CompanyStatsSummary'
import useModal from 'hooks/useModal'
import ExtendBookingModal from 'pages/booking/modals/ExtendBookingModal'
import OfficeEvaluationModal from 'pages/booking/modals/OfficeEvaluationModal'
import { getAjarleeServiceFee, getBookingSourceTranslation, getInsuranceDisplay, getOfficeDeliveryAmount, getPlatformTranslation } from 'pages/booking/bookingDetailHelpers'
import { BookingStatusBadge, PaymentStatusBadge } from 'pages/booking/BookingStatusBadges'
import { isCompanyDashboardUser, isPartnerDashboardUser, usesTenantDashboardUi } from 'lib/auth/dashboardRole'

const getBookingId = item => item?._id || item?.id

const BookingRowActionsMenuPortal = ({
  open,
  buttonRef,
  isRTL,
  item,
  t,
  history,
  isCompanyRole,
  hasAdminAccess,
  isPartnerOnly,
  canExtendBooking,
  setSelectedBooking,
  setIsDrawerOpen,
  setIsNotesDrawerOpen,
  setBookingToExtend,
  openExtendBookingModal,
  setBookingToEvaluate,
  openEvaluationModal,
  isEvaluated,
}) => {
  const [menuPosition, setMenuPosition] = useState(null)
  const bookingId = getBookingId(item)

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) {
      setMenuPosition(null)
      return
    }
    const rect = buttonRef.current.getBoundingClientRect()
    const ESTIMATED_MENU_HEIGHT = 200
    const spaceBelow = window.innerHeight - rect.bottom
    const openUpward = spaceBelow < ESTIMATED_MENU_HEIGHT

    setMenuPosition({
      ...(openUpward ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
      ...(isRTL ? { left: rect.left } : { right: window.innerWidth - rect.right }),
    })
  }, [open, isRTL, buttonRef])

  if (!open || typeof document === 'undefined') return null

  return createPortal(
    <Transition
      show={open}
      as={Fragment}
      enter="transition ease-out duration-100"
      enterFrom="transform opacity-0 scale-95"
      enterTo="transform opacity-100 scale-100"
      leave="transition ease-in duration-75"
      leaveFrom="transform opacity-100 scale-100"
      leaveTo="transform opacity-0 scale-95"
    >
      <Menu.Items
        static
        style={menuPosition ? { position: 'fixed', ...menuPosition, zIndex: 1250 } : undefined}
        className={classNames('min-w-[11rem] rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none', !menuPosition && 'invisible')}
      >
        <Menu.Item>
          {({ active }) => (
            <button
              type="button"
              disabled={!bookingId}
              onClick={() => bookingId && history.push(`/booking/${bookingId}`)}
              className={classNames(active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'flex w-full items-center px-4 py-2 text-start text-sm')}
            >
              {t('bookings.buttons.viewBooking')}
            </button>
          )}
        </Menu.Item>
        {(!isCompanyRole || hasAdminAccess) && !isPartnerOnly && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                disabled={!bookingId}
                onClick={() => bookingId && history.push(`/booking/edit/${bookingId}/booking-profile`)}
                className={classNames(active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'flex w-full items-center px-4 py-2 text-start text-sm')}
              >
                {t('bookings.buttons.editBooking')}
              </button>
            )}
          </Menu.Item>
        )}
        {!isPartnerOnly && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => {
                  setSelectedBooking(item)
                  setIsDrawerOpen(true)
                }}
                className={classNames(active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'flex w-full items-center px-4 py-2 text-start text-sm')}
              >
                {t('bookings.buttons.viewLogs')}
              </button>
            )}
          </Menu.Item>
        )}
        {(!isCompanyRole || hasAdminAccess) && !isPartnerOnly && canExtendBooking(item) && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => {
                  setBookingToExtend(item)
                  openExtendBookingModal()
                }}
                className={classNames(active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'flex w-full items-center px-4 py-2 text-start text-sm')}
              >
                Extend Booking
              </button>
            )}
          </Menu.Item>
        )}
        {!isPartnerOnly && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => {
                  setSelectedBooking(item)
                  setIsNotesDrawerOpen(true)
                }}
                className={classNames(active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'flex w-full items-center px-4 py-2 text-start text-sm')}
              >
                {t('bookings.buttons.viewNotes')}
              </button>
            )}
          </Menu.Item>
        )}
        {hasAdminAccess && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                disabled={!bookingId}
                onClick={() => {
                  setBookingToEvaluate(item)
                  openEvaluationModal()
                }}
                className={classNames(active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'flex w-full items-center justify-between gap-2 px-4 py-2 text-start text-sm')}
              >
                <span>{t('bookings.buttons.officeEvaluation')}</span>
                {isEvaluated && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-600">
                    <CheckCircleIcon className="h-4 w-4" />
                    {t('bookings.buttons.evaluated')}
                  </span>
                )}
              </button>
            )}
          </Menu.Item>
        )}
      </Menu.Items>
    </Transition>,
    document.body
  )
}

const BookingRowActionsMenu = props => {
  const buttonRef = useRef(null)

  return (
    <Menu as="div" className="relative inline-block text-start" onClick={e => e.stopPropagation()}>
      {({ open }) => (
        <>
          <Menu.Button
            ref={buttonRef}
            type="button"
            className="relative inline-flex items-center justify-center rounded-md border border-gray-300 bg-white p-1.5 text-gray-600 shadow-sm hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-dark-500 focus:ring-offset-1"
            aria-label={props.t('bookings.actions.openMenu')}
            title={props.isEvaluated ? props.t('bookings.buttons.evaluated') : props.t('bookings.actions.openMenu')}
          >
            <DotsHorizontalIcon className="h-5 w-5" aria-hidden strokeWidth={2} />
            {props.isEvaluated && (
              <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-green-500 ring-2 ring-white" title={props.t('bookings.buttons.evaluated')} />
            )}
          </Menu.Button>
          <BookingRowActionsMenuPortal open={open} buttonRef={buttonRef} {...props} />
        </>
      )}
    </Menu>
  )
}

// eslint-disable-next-line
const Bookings = forwardRef(
  (
    {
      setSearchKey,
      bookingCreatedBy,
      bookingStatus,
      paymentStatus,
      partner,
      bookDateTime,
      bookStartDateTime,
      bookEndDateTime,
      companyIds,
      companyGovernorates,
      companyMinAge,
      companyMaxAge,
      blockedCountries,
      instantBooking,
      ajarleePercentageMin,
      ajarleePercentageMax,
      companyDepositMin,
      companyDepositMax,
      vatTax,
      delivery,
      deliveryFeeMin,
      deliveryFeeMax,
      ...props
    },
    ref
  ) => {
    const history = useHistory()
    const { t, i18n } = useTranslation()
    const [isDrawerOpen, setIsDrawerOpen] = useState(false)
    const [isNotesDrawerOpen, setIsNotesDrawerOpen] = useState(false)
    const [selectedBooking, setSelectedBooking] = useState(null)
    const [bookingStatuses, setBookingStatuses] = useState({})
    const [bookingPayments, setBookingPayments] = useState({})
    const [streamConnected, setStreamConnected] = useState(false)
    const [bookingToExtend, setBookingToExtend] = useState(null)
    const [bookingToEvaluate, setBookingToEvaluate] = useState(null)
    const [evaluatedBookingIds, setEvaluatedBookingIds] = useState(() => new Set())
    const withReportsRef = useRef(null)
    const selectedBookingRef = useRef(null)
    const userFromStore = useSelector(state => get(state, 'user.user'))
    // eslint-disable-next-line
    const hasAdminAccess = userFromStore?.roles?.some(role => role.type === 'admin')
    const isCompanyRole = isCompanyDashboardUser(userFromStore)
    const isPartnerOnly = isPartnerDashboardUser(userFromStore)
    const usesTenantUi = usesTenantDashboardUi(userFromStore)
    const isRTL = i18n.language === 'ar'

    const hidesFailedPaymentBookings = isCompanyRole && !hasAdminAccess

    selectedBookingRef.current = selectedBooking

    const refreshEvaluationStatus = useCallback(
      async list => {
        if (!hasAdminAccess) return
        const ids = (list || []).map(getBookingId).filter(Boolean)
        if (!ids.length) return
        try {
          const response = await apiPost('office-evaluations/status', { bookingIds: ids })
          setEvaluatedBookingIds(new Set(response?.data?.evaluatedBookingIds || []))
        } catch (error) {}
      },
      [hasAdminAccess]
    )

    useEffect(() => {
      const qs = queryString.parse(window.location.search)

      if (qs?.bookingId) {
        setSelectedBooking({ _id: qs?.bookingId })
        setIsDrawerOpen(true)
        setIsNotesDrawerOpen(true)
      }
    }, [])

    const handleStatusChange = useCallback(
      (bookingId, status, bookingData) => {
        if (hidesFailedPaymentBookings && status === 'failedPayment') {
          if (withReportsRef.current) {
            withReportsRef.current.removeBooking(bookingId)
          }
          return
        }

        setBookingStatuses(prev => ({
          ...prev,
          [bookingId]: status,
        }))

        setBookingPayments(prev => ({
          ...prev,
          [bookingId]: bookingData.paymentStatus,
        }))

        if (selectedBookingRef.current && selectedBookingRef.current._id === bookingId) {
          setSelectedBooking(prev => ({
            ...prev,
            ...bookingData,
            status,
            paymentStatus: bookingData.paymentStatus,
          }))
        }
      },
      [hidesFailedPaymentBookings]
    )

    const handleConnectionChange = useCallback(connected => {
      setStreamConnected(connected)
    }, [])

    const handleNewBooking = useCallback(
      booking => {
        if (hidesFailedPaymentBookings && booking?.status === 'failedPayment') return
        if (withReportsRef.current && booking) {
          withReportsRef.current.addNewBooking(booking)
        }
      },
      [hidesFailedPaymentBookings]
    )

    const calculateAjarleeAmount = (totalPrice, percentage) => {
      if (!totalPrice || !percentage) return 0
      return formatPrice(totalPrice * (percentage / 100))
    }

    const calculateCompanyAmount = (totalPrice, percentage) => {
      if (!totalPrice || !percentage) return totalPrice || 0
      return formatPrice(totalPrice - totalPrice * (percentage / 100))
    }

    const handleInvoiceDownload = async bookingId => {
      if (!bookingId) return

      try {
        const blob = await apiGetBlob(`bookings/${bookingId}/invoice/download`)

        if (!blob) {
          return
        }

        const blobUrl = window.URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.href = blobUrl
        link.download = `booking_${bookingId}_invoice.pdf`
        link.target = '_blank'
        link.rel = 'noopener noreferrer'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        window.URL.revokeObjectURL(blobUrl)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error downloading invoice:', error)
      }
    }

    // const isBookingInPast = item => {
    //   const now = new Date()
    //   return new Date(item.rentalEndDate) < now
    // }

    const getStatusTranslation = status => {
      return t(`bookings.status.${status}`) || startCase(status)
    }

    const getPaymentStatusTranslation = paymentStatus => {
      return t(`bookings.paymentStatus.${paymentStatus}`) || startCase(paymentStatus)
    }

    const getCreatedByTranslation = createdBy => {
      return t(`bookings.createdBy.${toLower(createdBy)}`) || startCase(toLower(createdBy))
    }

    const getPaymentMethodTranslation = paymentMethod => {
      return t(`bookings.paymentMethod.${toLower(paymentMethod)}`) || startCase(toLower(paymentMethod))
    }

    const getDeliveryTypeTranslation = deliveryType => {
      const normalizedType = toLower(deliveryType || '').replace(/[\s_-]/g, '')

      if (!normalizedType) {
        return ''
      }

      if (normalizedType === 'pickupmyself') {
        return t('bookings.deliveryType.pickUpMyself')
      }

      if (normalizedType === 'delivertome') {
        return t('bookings.deliveryType.deliverToMe')
      }

      const translationKey = `bookings.deliveryType.${normalizedType}`
      const translatedValue = t(translationKey)

      if (translatedValue !== translationKey) {
        return translatedValue
      }

      return startCase(deliveryType)
    }

    const canExtendBooking = item => {
      const bookingStatus = bookingStatuses[item._id] || bookingStatuses[item.id] || item?.status
      const payment = bookingPayments[item._id] || bookingPayments[item.id] || item?.paymentStatus
      return bookingStatus === 'confirmed' && payment === 'paid'
    }

    const BookingCard = ({ item }) => {
      const bookingStatus = bookingStatuses[item._id] || bookingStatuses[item.id] || item?.status
      const paymentStatus = bookingPayments[item._id] || bookingPayments[item.id] || item?.paymentStatus
      const isCancelled = bookingStatus === 'cancelled' || bookingStatus === 'rejected' || bookingStatus === 'failedPayment'
      const isRTL = i18n.language === 'ar'

      const isArabic = i18n.language === 'ar'
      const make = usesTenantUi && isArabic ? item?.rentalCar?.car?.make?.makeAr || item?.rentalCar?.car?.make?.make || '' : item?.rentalCar?.car?.make?.make || ''
      const model = usesTenantUi && isArabic ? item?.rentalCar?.car?.model?.modalAr || item?.rentalCar?.car?.model?.modal || '' : item?.rentalCar?.car?.model?.modal || ''
      const carName = startCase(toLower(`${make} ${model} ${item?.rentalCar?.modelYear || ''}`))

      const refLabel = _.trim(item?.bookingReferenceId) || '—'
      const customerName = item?.user?.fullName || item?.user?.email || item?.user?.phone || '—'
      const getInitials = name => {
        if (!name || !_.isString(name)) return '?'
        const parts = _.compact(_.split(_.trim(name), /\s+/))
        if (parts.length >= 2) return _.toUpper(`${parts[0][0]}${parts[parts.length - 1][0]}`)
        return _.toUpper(parts[0].slice(0, 2))
      }

      const combinedStatusLabel = item?.isOfficeBlock ? t('bookings.status.officeBlock') : `${getStatusTranslation(bookingStatus)} / ${getPaymentStatusTranslation(paymentStatus)}`
      const combinedBadgeClass = classNames('px-2 py-0.5 text-[9px] font-bold rounded-full uppercase tracking-wide max-w-full truncate ring-1 ring-inset', {
        'bg-rose-100 text-rose-900 ring-rose-200/80': isCancelled,
        'bg-sky-100 text-sky-900 ring-sky-200/70': !isCancelled && bookingStatus === 'confirmed' && paymentStatus !== 'paid',
        'bg-teal-100 text-teal-900 ring-teal-400/60': !isCancelled && paymentStatus === 'paid',
        'bg-orange-100 text-orange-950 ring-orange-300/80': !isCancelled && bookingStatus === 'pending',
        'bg-slate-100 text-slate-700 ring-slate-200/80': !isCancelled && paymentStatus !== 'paid' && bookingStatus !== 'confirmed' && bookingStatus !== 'pending',
      })

      // Border colors aligned with backend BookingStatus / PaymentStatus (see RentalCar model)
      const cardBorderClass = (() => {
        const bs = bookingStatus
        const ps = paymentStatus

        if (bs === 'cancelled' || bs === 'rejected' || bs === 'failedPayment') {
          return 'border-rose-400/80 hover:border-rose-500 shadow-[inset_0_0_0_1px_rgba(244,63,94,0.08)]'
        }
        if (ps === 'failure') {
          return 'border-red-500/85 hover:border-red-600 shadow-[inset_0_0_0_1px_rgba(239,68,68,0.1)]'
        }
        if (ps === 'refund') {
          return 'border-fuchsia-400/85 hover:border-fuchsia-500 shadow-[inset_0_0_0_1px_rgba(192,38,211,0.08)]'
        }
        if (ps === 'paid') {
          return 'border-teal-400/90 hover:border-teal-500 shadow-[0_0_0_1px_rgba(45,212,191,0.14)]'
        }
        if (ps === 'partialpaid') {
          return 'border-amber-400/90 hover:border-amber-500 shadow-[0_0_0_1px_rgba(251,191,36,0.16)]'
        }
        if (bs === 'waiting_for_payment_approval' || ps === 'waiting_for_payment_approval') {
          return 'border-violet-400/90 hover:border-violet-500 shadow-[0_0_0_1px_rgba(167,139,250,0.18)]'
        }
        if (bs === 'completed') {
          return 'border-emerald-500/85 hover:border-emerald-600 shadow-[inset_0_0_0_1px_rgba(16,185,129,0.1)]'
        }
        if (bs === 'inProgress') {
          return 'border-blue-500/85 hover:border-blue-600 shadow-[0_0_0_1px_rgba(59,130,246,0.14)]'
        }
        if (bs === 'underReview') {
          return 'border-indigo-500/85 hover:border-indigo-600 shadow-[0_0_0_1px_rgba(99,102,241,0.14)]'
        }
        if (bs === 'confirmed' && ps !== 'paid') {
          return 'border-sky-400/90 hover:border-sky-500 shadow-[0_0_0_1px_rgba(56,189,248,0.14)]'
        }
        if (bs === 'pending') {
          return 'border-orange-400/90 hover:border-orange-500 shadow-[0_0_0_1px_rgba(251,146,60,0.18)]'
        }
        if (ps === 'unpaid') {
          return 'border-slate-400/85 hover:border-slate-500 shadow-[inset_0_0_0_1px_rgba(100,116,139,0.12)]'
        }
        return 'border-slate-300/80 hover:border-slate-400'
      })()

      const TimelineRow = ({ icon: Icon, label, dateLine, timeLine, iconClass }) => (
        <div className="flex gap-2">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-white text-dark-600 shadow-sm ring-1 ring-slate-200/90">
            <Icon className={classNames('h-4 w-4', iconClass)} strokeWidth={1.75} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline justify-between gap-1">
              <span className="text-[11px] font-semibold text-slate-600">{label}</span>
              <div className={classNames('min-w-0', isRTL ? 'text-start' : 'text-end')}>
                <span className="text-[11px] font-bold text-slate-900">{dateLine}</span>
                {timeLine ? <span className="block text-[10px] font-semibold text-slate-500">{timeLine}</span> : null}
              </div>
            </div>
          </div>
        </div>
      )

      return (
        <div
          className={classNames(
            'group flex flex-col gap-2.5 rounded-2xl border-2 bg-white p-3.5 shadow-sm transition-all duration-300 hover:shadow-md sm:p-4',
            cardBorderClass,
            isCancelled && 'opacity-80 grayscale transition-all hover:opacity-100 hover:grayscale-0'
          )}
        >
          <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-start">
            <div className="min-w-0 space-y-0.5">
              <h3 className="truncate text-base font-extrabold leading-snug tracking-tight text-slate-900 transition-colors group-hover:text-dark-600">{carName}</h3>
              <div className="font-mono text-[10px] font-bold text-slate-500">
                {t('booking.labels.bookingReferenceId')} {refLabel}
              </div>
            </div>
            <div className="flex flex-col items-start gap-1.5 sm:items-end">
              <span className={combinedBadgeClass} title={combinedStatusLabel}>
                {combinedStatusLabel}
              </span>
              {item?.user ? (
                <div className="flex -space-x-2 rtl:space-x-reverse">
                  <div
                    className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-sky-100 text-[9px] font-bold text-sky-800 shadow-sm ring-1 ring-slate-100"
                    title={customerName}
                  >
                    {getInitials(customerName)}
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div className="flex flex-col justify-between gap-2 rounded-lg bg-slate-50/90 p-2.5 ring-1 ring-slate-200/60 sm:flex-row sm:items-center">
            {item?.user ? (
              <div className="min-w-0 space-y-0.5">
                <div className="text-[9px] font-bold uppercase tracking-wide text-slate-500">{t('bookings.card.customer')}</div>
                <div className="text-sm font-bold text-slate-900">{customerName}</div>
                {(hasAdminAccess || _.includes(['confirmed', 'completed'], bookingStatus)) && item?.user?.phone ? (
                  <div className="text-[11px] text-slate-600" dir="ltr">
                    {item.user.phone}
                  </div>
                ) : null}
              </div>
            ) : null}
            <div className={classNames('min-w-0 text-start', item?.user && 'sm:text-end')}>
              <div className="text-[9px] font-bold uppercase tracking-wide text-slate-500">{t('bookings.card.createdBy')}</div>
              <div className="text-[11px] font-semibold text-slate-800">{getCreatedByTranslation(item?.bookingCreatedBy || '-')}</div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1.5 text-[9px] font-extrabold uppercase tracking-widest text-slate-500">
              <span className="h-px w-3 bg-slate-300" aria-hidden />
              {t('bookings.card.bookingTimeline')}
            </div>
            <div className="space-y-2.5">
              <TimelineRow
                icon={CalendarIcon}
                label={t('bookings.card.bookingDate')}
                dateLine={moment.parseZone(item.bookingDate).format('MMM D, YYYY')}
                timeLine={moment.parseZone(item.bookingDate).format('h:mm A')}
                iconClass="text-dark-600"
              />
              <TimelineRow
                icon={PlayIcon}
                label={t('bookings.card.rentalStartDate')}
                dateLine={item.rentalStartDate ? moment.parseZone(item.rentalStartDate).format('MMM D, YYYY') : '—'}
                timeLine={item.rentalStartDate ? moment.parseZone(item.rentalStartDate).format('h:mm A') : undefined}
                iconClass="text-emerald-600"
              />
              <TimelineRow
                icon={StopIcon}
                label={t('bookings.card.rentalEndDate')}
                dateLine={item.rentalEndDate ? moment.parseZone(item.rentalEndDate).format('MMM D, YYYY') : '—'}
                timeLine={item.rentalEndDate ? moment.parseZone(item.rentalEndDate).format('h:mm A') : undefined}
                iconClass="text-rose-600"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-1.5 border-t border-slate-200/80 pt-2.5">
            <div className="text-center">
              <div className="mb-0.5 text-[9px] font-bold text-slate-500">{t('bookings.card.totalAmount')}</div>
              <div className={classNames('text-xs font-black', isCancelled ? 'text-rose-600' : 'text-dark-600')}>
                {formatPrice(isPartnerOnly || !usesTenantUi ? item.totalPrice : item.companyTotal, true)} {usesTenantUi || isPartnerOnly ? t('common.omr') : 'OMR'}
              </div>
            </div>
            <div className="border-x border-slate-200/80 px-1.5 text-center">
              <div className="mb-0.5 text-[9px] font-bold text-slate-500">{t('bookings.card.paymentMethod')}</div>
              <div className="truncate text-xs font-bold text-slate-900">{getPaymentMethodTranslation(item.paymentMethod)}</div>
            </div>
            <div className="text-center">
              <div className="mb-0.5 text-[9px] font-bold text-slate-500">{t('bookings.card.deliveryType')}</div>
              <div className="truncate text-xs font-bold text-slate-900">{getDeliveryTypeTranslation(item?.delivery?.type)}</div>
            </div>
          </div>

          <div className="mt-auto grid grid-cols-2 gap-1.5 border-t border-slate-200/80 pt-2.5">
            <button
              type="button"
              onClick={() => history.push(`/booking/${item._id}`)}
              className="rounded-md bg-dark-600 px-2.5 py-2 text-[10px] font-bold uppercase tracking-tight text-white transition-colors hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-dark-500 focus:ring-offset-1"
            >
              {t('bookings.buttons.viewDetails')}
            </button>
            {(!usesTenantUi || hasAdminAccess) && (
              <button
                type="button"
                onClick={() => history.push(`/booking/edit/${item._id}/booking-profile`)}
                className="rounded-md bg-slate-100 px-2.5 py-2 text-[10px] font-bold uppercase tracking-tight text-slate-800 transition-colors hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-400/40 focus:ring-offset-1"
              >
                {t('bookings.buttons.editBooking')}
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setSelectedBooking(item)
                setIsDrawerOpen(true)
              }}
              className="rounded-md bg-slate-100 px-2.5 py-2 text-[10px] font-bold uppercase tracking-tight text-slate-800 transition-colors hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-400/40 focus:ring-offset-1"
            >
              {t('bookings.buttons.viewLogs')}
            </button>
            <button
              type="button"
              onClick={() => {
                setSelectedBooking(item)
                setIsNotesDrawerOpen(true)
              }}
              className={classNames(
                'rounded-md bg-slate-50 px-2.5 py-2 text-[10px] font-bold uppercase tracking-tight text-slate-600 ring-1 ring-slate-200/80 transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300/60',
                !item?.invoiceUrl && 'col-span-2'
              )}
            >
              {t('bookings.buttons.viewNotes')}
            </button>
            {item?.invoiceUrl ? (
              <button
                type="button"
                onClick={() => handleInvoiceDownload(item._id || item.id)}
                className="flex items-center justify-center gap-1.5 rounded-md bg-slate-50 px-2.5 py-2 text-[10px] font-bold uppercase tracking-tight text-slate-600 ring-1 ring-slate-200/80 transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300/60"
              >
                <DownloadIcon className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
                <span className="truncate">{t('bookings.buttons.downloadInvoice')}</span>
              </button>
            ) : null}
          </div>
        </div>
      )
    }

    const columns = [
      {
        name: t('bookings.columns.user'),
        width: '10%',
        key: 'type',
        sortable: false,
        rendered: item => (
          <div className="text-xs text-blue-500 font-mono">
            {item?.user ? (
              <Tooltip
                color="white"
                overlayClassName="!min-w-[400px] !w-full"
                title={
                  <div className="flex flex-col gap-2 p-2">
                    <div className="flex border rounded p-2 flex-col text-[12px] text-gray-500">
                      <b className="mb-1.5 border-b">{t('bookings.tooltips.userDetails')}</b>
                      <div className="text-xs font-mono">
                        {item?.user ? (
                          <>
                            <div>
                              {t('bookings.tooltips.name')}{' '}
                              <a target="_blank" className="text-blue-500" href={`/users/edit/${item?.user?._id}`} rel="noreferrer">
                                {item?.user?.fullName}
                              </a>
                            </div>
                            <div className="mt-1">
                              {t('bookings.tooltips.email')} <span className="text-black">{item?.user?.email}</span>{' '}
                            </div>
                            {/* eslint-disable-next-line */}
                            {hasAdminAccess || _.includes(['confirmed', 'completed'], bookingStatuses[item._id] || item?.status) ? (
                              <div className="mt-1">
                                {t('bookings.tooltips.phone')} <span className="text-black">{item?.user?.phone}</span>{' '}
                              </div>
                            ) : null}
                            <div className="mt-1">
                              {t('bookings.tooltips.nationality')} <span className="text-black">{item?.user?.nationality}</span>{' '}
                            </div>
                          </>
                        ) : (
                          ''
                        )}
                      </div>
                    </div>
                  </div>
                }
              >
                <a target="_blank" className="text-blue-500" href={`/users/edit/${item?.user?._id}`} rel="noreferrer">
                  {item?.user?.fullName || item?.user?.email || item?.user?.phone}
                </a>
              </Tooltip>
            ) : (
              ''
            )}
          </div>
        ),
      },
      {
        name: t('bookings.columns.car'),
        width: '10%',
        key: 'stripe',
        sortable: false,
        rendered: item => {
          return (
            <div className="text-xs text-blue-500 font-mono">
              {item ? (
                <a target="_blank" href={`/car/edit/${item?.rentalCar?._id}/car-profile`} rel="noreferrer">
                  {item?.rentalCar?.car?.make?.make || ''} {item?.rentalCar?.car?.model?.modal || ''} {item?.rentalCar?.modelYear}
                </a>
              ) : (
                ''
              )}
            </div>
          )
        },
      },
      {
        name: t('bookings.columns.company'),
        width: '10%',
        key: 'stripe',
        sortable: false,
        rendered: item => {
          return (
            <div className="text-xs text-blue-500 font-mono">
              {item?.rentalCar?.company ? (
                <a target="_blank" href={`/company/edit/${item?.rentalCar?.company?._id}/company-profile`} rel="noreferrer">
                  {item?.rentalCar?.company?.name}
                </a>
              ) : (
                ''
              )}
            </div>
          )
        },
      },
      {
        name: t('bookings.columns.branch'),
        width: '10%',
        key: 'stripe',
        sortable: false,
        rendered: item => {
          return <div className="text-xs text-blue-500 font-mono">{item?.branch?.name}</div>
        },
      },
      {
        name: t('bookings.columns.refrenceId'),
        width: '10%',
        key: 'stripe',
        sortable: false,
        rendered: item => {
          return <div className="text-xs text-blue-500 font-mono">{item?.bookingReferenceId}</div>
        },
      },
      {
        name: t('bookings.columns.invoice'),
        width: '10%',
        key: 'name',
        sortable: false,
        rendered: item => {
          if (item?.invoiceUrl) {
            return (
              <div>
                <button onClick={() => handleInvoiceDownload(item._id || item.id)} className="text-sm text-normal text-blue-400 hover:text-blue-600 underline cursor-pointer">
                  {t('bookings.buttons.downloadInvoice')}
                </button>
              </div>
            )
          }
        },
      },
      {
        name: t('bookings.columns.bookingStatus'),
        width: '10%',
        key: 'status',
        sortable: true,
        rendered: item => <BookingStatusBadge status={bookingStatuses[item._id] || bookingStatuses[item.id] || item?.status} isOfficeBlock={item?.isOfficeBlock} />,
      },

      {
        name: t('bookings.columns.createdBy'),
        width: '10%',
        key: 'bookingCreatedBy',
        sortable: true,
        rendered: item => (
          <span
            className={`text-xs font-regular ${
              toLower(item.bookingCreatedBy) === 'user'
                ? 'bg-green-100 text-green-700'
                : toLower(item.bookingCreatedBy) === 'office'
                ? 'bg-yellow-100 text-yellow-800'
                : toLower(item.bookingCreatedBy) === 'admin'
                ? 'bg-gray-200 text-gray-700'
                : toLower(item.bookingCreatedBy) === 'partner'
                ? 'bg-gray-200 text-blue-700'
                : 'bg-gray-200 text-gray-700'
            } px-2 py-1 rounded-md`}
          >
            {getCreatedByTranslation(item.bookingCreatedBy)}
          </span>
        ),
      },
      {
        name: t('bookings.columns.partnerName'),
        width: '10%',
        key: 'partnerName',
        sortable: false,
        rendered: item => {
          return (
            <div>
              <p>{item?.partner?.name}</p>
            </div>
          )
        },
      },
      {
        name: t('bookings.columns.paymentMethod'),
        width: '10%',
        key: 'paymentMethod',
        sortable: true,
        rendered: item => (
          <span
            className={`text-xs font-regular ${
              toLower(item.paymentMethod) === 'card'
                ? 'bg-green-100 text-green-700'
                : toLower(item.paymentMethod) === 'cash'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-gray-200 text-gray-700'
            } px-2 py-1 rounded-md`}
          >
            {getPaymentMethodTranslation(item.paymentMethod)}
          </span>
        ),
      },
      {
        name: t('bookings.columns.bookingSource'),
        width: '8%',
        key: 'bookingSource',
        sortable: false,
        rendered: item => (
          <div>
            <p>{getBookingSourceTranslation(t, item.bookingSource, item.platform)}</p>
          </div>
        ),
      },
      {
        name: t('bookings.columns.platform'),
        width: '8%',
        key: 'platform',
        sortable: false,
        rendered: item => (
          <div>
            <p>{getPlatformTranslation(t, item.platform)}</p>
          </div>
        ),
      },
      {
        name: t('bookings.columns.deliveryType'),
        width: '10%',
        key: 'deliveryType',
        sortable: false,
        rendered: item => {
          return (
            <div>
              <p>{getDeliveryTypeTranslation(item?.delivery?.type)}</p>
            </div>
          )
        },
      },
      {
        name: t('bookings.columns.amount'),
        width: '10%',
        key: 'amount',
        sortable: false,
        rendered: item => (
          <Tooltip
            color="white"
            overlayClassName="!min-w-[270px] !w-full"
            title={
              <div className="flex flex-col gap-2">
                <div className="flex border rounded p-2 flex-col text-[12px] text-gray-500">
                  <b className="mb-1.5 border-b">{t('bookings.tooltips.paymentBreakdown')}</b>
                  <div className="text-xs font-mono">
                    {item ? (
                      <>
                        <div>
                          <div className="mt-1 grid grid-cols-2">
                            {t('bookings.tooltips.carPrice')} <span className="text-black">{formatPrice(Number(item?.priceWithoutTax) || 0, true)} OMR</span>
                          </div>
                          {item?.priceWithoutTax !== item?.rentalCarDiscountPrice && (
                            <div className="mt-1 grid grid-cols-2">
                              {t('bookings.tooltips.discountedPrice')} <span className="text-black">{formatPrice(Number(item?.rentalCarDiscountPrice) || 0, true)} OMR</span>
                            </div>
                          )}
                        </div>
                        <div className="mt-1 grid grid-cols-2">
                          {t('bookings.tooltips.depositAmount')} <span className="text-black">{formatPrice(item?.depositAmount || 0, true)} OMR</span>{' '}
                        </div>
                        <div className="mt-1 grid grid-cols-2">
                          {t('bookings.tooltips.deliveryFee')} <span className="text-black">{formatPrice(getOfficeDeliveryAmount(item), true)} OMR</span>{' '}
                        </div>
                        <div className="mt-1 grid grid-cols-2">
                          {t('bookings.tooltips.serviceFee')} <span className="text-black">{formatPrice(getAjarleeServiceFee(item), true)} OMR</span>{' '}
                        </div>
                        <div className="mt-1 grid grid-cols-2">
                          {t('bookings.tooltips.insuranceFee')} <span className="text-black">{formatPrice(getInsuranceDisplay(item).displayAmount, true)} OMR</span>{' '}
                        </div>
                        <div className="mt-1 mb-2 grid grid-cols-2">
                          {t('bookings.tooltips.vatTax')} <span className="text-black">{formatPrice(item?.vatTax || 0, true)} OMR</span>{' '}
                        </div>
                        <div className="mt-1 grid border-t pt-1 grid-cols-2">
                          {t('bookings.tooltips.totalAmount')} <span className="text-black">{formatPrice(item?.totalPrice || 0, true)} OMR</span>{' '}
                        </div>
                        <div className="mt-1 grid pt-1 grid-cols-2">
                          Ajarlee {item?.ajarleePercentage}%:{' '}
                          <span className="text-black">{formatPrice(Number(calculateAjarleeAmount(item?.totalPrice, item?.ajarleePercentage)) || 0, true)} OMR</span>
                        </div>
                        <div className="mt-1 grid border-t pt-1 grid-cols-2">
                          {t('bookings.tooltips.companyAmount')}{' '}
                          <span className="text-black">{formatPrice(Number(calculateCompanyAmount(item?.totalPrice, item?.ajarleePercentage)) || 0, true)} OMR</span>
                        </div>
                      </>
                    ) : (
                      t('bookings.tooltips.noPaymentDetails')
                    )}
                  </div>
                </div>
              </div>
            }
          >
            <span className={`text-xs font-medium text-gray-500 py-1 rounded-md`}>{formatPrice(item.totalPrice, true)} OMR</span>
          </Tooltip>
        ),
      },
      {
        name: t('bookings.columns.paymentStatus'),
        width: '10%',
        key: 'paymentStatus',
        sortable: true,
        rendered: item => <PaymentStatusBadge status={bookingPayments[item._id] || bookingPayments[item.id] || item.paymentStatus} />,
      },
      {
        name: t('bookings.columns.bookingDate'),
        width: '5%',
        key: 'bookingDate',
        align: 'left',
        sortable: true,
        rendered: item => (
          <div className="text-sm text-gray-500">
            <Tooltip title={moment.parseZone(item.bookingDate).format('MMMM Do YYYY, h:mm A')}>{moment.parseZone(item.bookingDate).fromNow()}</Tooltip>
          </div>
        ),
      },
      {
        name: t('bookings.columns.bookingStartDate'),
        width: '5%',
        key: 'rentalStartDate',
        align: 'left',
        sortable: true,
        rendered: item =>
          item.rentalStartDate && (
            <div className="text-sm text-gray-500">
              <Tooltip title={moment.parseZone(item.rentalStartDate).format('MMMM Do YYYY, h:mm A')}>{moment.parseZone(item.rentalStartDate).fromNow()}</Tooltip>
            </div>
          ),
      },
      {
        name: t('bookings.columns.bookingEndDate'),
        width: '5%',
        key: 'rentalEndDate',
        align: 'left',
        sortable: true,
        rendered: item =>
          item.rentalEndDate && (
            <div className="text-sm text-gray-500 flex gap-1">
              <Tooltip title={moment.parseZone(item.rentalEndDate).format('MMMM Do YYYY, h:mm A')}>{moment.parseZone(item.rentalEndDate).fromNow()}</Tooltip>
            </div>
          ),
      },
      {
        name: t('bookings.columns.action'),
        width: '5%',
        key: 'completedAt',
        align: 'left',
        rendered: item => (
          <BookingRowActionsMenu
            item={item}
            isRTL={isRTL}
            t={t}
            history={history}
            isCompanyRole={isCompanyRole}
            hasAdminAccess={hasAdminAccess}
            isPartnerOnly={isPartnerOnly}
            canExtendBooking={canExtendBooking}
            setSelectedBooking={setSelectedBooking}
            setIsDrawerOpen={setIsDrawerOpen}
            setIsNotesDrawerOpen={setIsNotesDrawerOpen}
            setBookingToExtend={setBookingToExtend}
            openExtendBookingModal={openExtendBookingModal}
            setBookingToEvaluate={setBookingToEvaluate}
            openEvaluationModal={openEvaluationModal}
            isEvaluated={evaluatedBookingIds.has(getBookingId(item))}
          />
        ),
      },
    ]

    const closeDrawer = () => {
      setIsDrawerOpen(false)
      setIsNotesDrawerOpen(false)
      setSelectedBooking(null)
    }

    const [ExtendBookingModalShell, openExtendBookingModal] = useModal({
      content: (
        <ExtendBookingModal
          bookingData={bookingToExtend}
          bookingId={bookingToExtend?._id || bookingToExtend?.id}
          onSuccess={data => {
            if (data?.id) {
              history.push(`/booking/${data.id}`)
            }
          }}
        />
      ),
      title: 'Extend Booking',
      dimensions: {
        height: '220px',
        width: '540px',
      },
    })

    const [OfficeEvaluationModalShell, openEvaluationModal] = useModal({
      content: (
        <OfficeEvaluationModal
          bookingId={bookingToEvaluate?._id || bookingToEvaluate?.id}
          onSaved={() => {
            const id = bookingToEvaluate?._id || bookingToEvaluate?.id
            if (id) setEvaluatedBookingIds(prev => new Set(prev).add(String(id)))
          }}
        />
      ),
      title: t('officeEvaluation.title'),
      dimensions: {
        height: '600px',
        width: '640px',
      },
    })

    return (
      <>
        <CompanyStatsSummary variant="bookings" streamConnected={streamConnected} />
        <WithReports
          {...props}
          setSearchKey={setSearchKey}
          onDataUpdate={refreshEvaluationStatus}
          apiURL="bookings"
          defaultFileName={t('bookings.title')}
          forwardedRef={withReportsRef}
          ref={ref}
          columns={columns}
          activeFilterName="past_7_days"
          streamConnected={streamConnected}
          suppressStreamBanner={usesTenantUi}
          bookingCreatedBy={bookingCreatedBy}
          bookingStatus={bookingStatus}
          paymentStatus={paymentStatus}
          partner={partner}
          bookDateTime={bookDateTime}
          bookStartDateTime={bookStartDateTime}
          bookEndDateTime={bookEndDateTime}
          companyIds={companyIds}
          companyGovernorates={companyGovernorates}
          companyMinAge={companyMinAge}
          companyMaxAge={companyMaxAge}
          blockedCountries={blockedCountries}
          instantBooking={instantBooking}
          ajarleePercentageMin={ajarleePercentageMin}
          ajarleePercentageMax={ajarleePercentageMax}
          companyDepositMin={companyDepositMin}
          companyDepositMax={companyDepositMax}
          vatTax={vatTax}
          delivery={delivery}
          deliveryFeeMin={deliveryFeeMin}
          deliveryFeeMax={deliveryFeeMax}
          // Pass the view type and card component to WithReports
          viewType={usesTenantUi ? 'card' : 'table'}
          CardComponent={BookingCard}
        />

        <BookingLogsDrawer isDrawerOpen={isDrawerOpen} closeDrawer={closeDrawer} selectedBooking={selectedBooking} />
        <BookingNotesDrawer isNotesDrawerOpen={isNotesDrawerOpen} closeDrawer={closeDrawer} selectedBooking={selectedBooking} />
        <ExtendBookingModalShell />
        <OfficeEvaluationModalShell />

        <BookingsStream
          id={selectedBooking?._id || selectedBooking?.id}
          onStatusChange={handleStatusChange}
          onConnectionChange={handleConnectionChange}
          onNewBooking={handleNewBooking}
          url={`${process.env.REACT_APP_BASE_URL}/admin/bookings/stream`}
        />
      </>
    )
  }
)

export default Bookings
